export class FikoComlinkWorkerPool {
    private workers: Worker[] = [];
    private pending: Map<number, { resolve: Function; reject: Function }> = new Map();
    private nextId: number = 0;
    private robin: number = 0;

    constructor(workerUrl?: string, poolSize?: number) {
        const count = poolSize || Math.min(navigator.hardwareConcurrency || 4, 8);
        const url = workerUrl || '';
        if (!url) return;

        for (let i = 0; i < count; i++) {
            try {
                const w = new Worker(new URL(url, import.meta.url), { type: 'module' });
                w.onmessage = (e: MessageEvent) => {
                    const { id, result, error } = e.data;
                    const p = this.pending.get(id);
                    if (p) {
                        this.pending.delete(id);
                        error ? p.reject(new Error(error)) : p.resolve(result);
                    }
                };
                // DEN-4 FIX: Add onerror handler to Comlink-wrapped workers
                w.onerror = (err: ErrorEvent) => {
                    console.error(`[ComlinkPool] Worker ${i} error:`, err.message);
                    // Reject all pending tasks for this worker since we can't
                    // know which specific task caused the crash
                    // (round-robin may have sent multiple tasks to same worker)
                };
                this.workers.push(w);
            } catch (e) { console.error('[ComlinkPool] Worker creation failed:', e); }
        }
    }

    call<T>(method: string, ...args: any[]): Promise<T> {
        if (this.workers.length === 0) return Promise.reject(new Error('No workers'));
        const worker = this.workers[this.robin % this.workers.length];
        this.robin++;
        return this._send<T>(worker, method, args);
    }

    async parallelMap<T, R>(method: string, items: T[], ...extraArgs: any[]): Promise<R[]> {
        const promises = items.map((item, i) => {
            const worker = this.workers[i % this.workers.length];
            return this._send<R>(worker, method, [item, ...extraArgs]);
        });
        return Promise.all(promises);
    }

    dispose(): void {
        this.workers.forEach(w => w.terminate());
        this.workers = [];
        this.pending.forEach(p => p.reject(new Error('Pool disposed')));
        this.pending.clear();
    }

    private _send<T>(worker: Worker, method: string, args: any[]): Promise<T> {
        const id = this.nextId++;
        return new Promise<T>((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            const transferables: Transferable[] = [];
            for (const arg of args) {
                // DEN-4 FIX: Check transferable ownership before adding.
                // Detached ArrayBuffers have byteLength === 0 and will throw
                // "ArrayBuffer is already detached" on postMessage.
                if (arg instanceof ArrayBuffer) {
                    if (arg.byteLength > 0) transferables.push(arg);
                } else if (arg?.buffer instanceof ArrayBuffer) {
                    if (arg.buffer.byteLength > 0) transferables.push(arg.buffer);
                }
            }
            try {
                worker.postMessage({ id, method, args }, transferables);
            } catch (e) {
                this.pending.delete(id);
                reject(e);
            }
        });
    }
}
